<template>
  <div class="gs-contents-step">
    <div class="gs-wizard-card gs-contents-step__card">
      <div class="gs-contents-step__head">
        <div>
          <h2>Content settings</h2>
          <p>Configure customer-facing pages, refund terms, and support information.</p>
        </div>
      </div>

      <div class="gs-contents-step__sections">
        <button
          v-for="section in contentSections"
          :key="section.value"
          type="button"
          class="gs-contents-step__section"
          :class="{ 'is-active': showContentsEditorDrawer && contentsTab === section.value, 'has-error': contentsErrorTabs.includes(section.value) }"
          :aria-invalid="contentsErrorTabs.includes(section.value) ? 'true' : 'false'"
          @click="handleContentsTabChange(section.value)"
        >
          <div>
            <strong>{{ section.title }}</strong>
            <span>{{ section.summary }}</span>
            <span v-if="contentsErrorTabs.includes(section.value)" class="gs-contents-step__error-copy">
              {{ contentSectionError(section.value) }}
            </span>
          </div>
          <em>{{ showContentsEditorDrawer && contentsTab === section.value ? 'Editing' : 'Edit' }}</em>
        </button>
      </div>
    </div>

    <gs-drawer
      v-model="showContentsEditorDrawer"
      size="xl"
      :title="activeContentSection.title"
      :subtitle="activeContentSection.summary"
    >
      <section class="gs-contents-step__drawer">
        <div class="gs-contents-step__drawer-body">
        <sales-page-tab v-if="contentsTab === 'salespage'" @update-model="handleUpdateModel"
          :sales_page="localModel.sales_page" :validation="$v" />

        <thankyou-page-tab v-else-if="contentsTab === 'thankyoupage'" @update-model="handleUpdateModel"
          :pricing_points="localModel.pricing_points" :validation="$v"/>

        <refund-page-tab v-else-if="contentsTab === 'refund'" @update-model="handleUpdateModel"
          :refund_period="localModel.refund_period" :waiting_period="localModel.waiting_period" :validation="$v" />

        <support-info-tab v-else-if="contentsTab === 'support_info'" :is_support_enabled="localModel.is_support_enabled"
          :support_info_type="localModel.support_info_type" :support_info_value="localModel.support_info_value"
          @update-model="handleUpdateModel" :validation="$v"/>
        </div>
      </section>
    </gs-drawer>
  </div>
</template>

<script>
import {
  required,
  minLength,
  numeric,
  url,
  minValue,
} from 'vuelidate/lib/validators';

import SalesPageTab from './tabs/contents/SalesPageTab';
import ThankyouPageTab from './tabs/contents/ThankyouPageTab';
import RefundPageTab from './tabs/contents/RefundPageTab';
import SupportInfoTab from './tabs/contents/SupportInfoTab';

export default {
  name: 'Step4',
  components: {
    SalesPageTab,
    ThankyouPageTab,
    RefundPageTab,
    SupportInfoTab,
  },
  props: {
    model: {
      required: true,
    },
  },
  data() {
    return {
      localModel: this.model,
      thankyouPageIndex: 0,
      contentsTabs: [
        {
          title: this.$t('frontend_product_step4_sales_page'),
          value: 'salespage',
        },
        {
          title: this.$t('frontend_product_step4_thankyou_page'),
          value: 'thankyoupage',
        },
        {
          title: this.$t('frontend_product_step4_refund_period'),
          value: 'refund',
        },
        {
          title: this.$t('frontend_product_step4_support_info'),
          value: 'support_info',
        },
      ],
      contentsTab: 'salespage',
      showContentsEditorDrawer: false,
    };
  },
  validations: {
    localModel: {
      sales_page: {
        required,
        url,
      },
      refund_period: {
        required,
        numeric,
        minValue: minValue(0),
      },
      waiting_period: {
        required,
        numeric,
        minValue: minValue(0),
      },
      support_info_value: {
        validSupportInfo(value, object) {
          let self = this;

          if (self.localModel.is_support_enabled) {
            if (value) {
              try {
                const parsed = JSON.parse(value);
                if (parsed && Array.isArray(parsed.channels)) {
                  return parsed.channels.every((channel) => {
                    return channel.name && channel.type && channel.value;
                  });
                }
              } catch (e) {
                // Legacy support info values are validated below.
              }

              if (self.localModel.support_info_type == 1) {
                var pattern = new RegExp(
                  '^(https?:\\/\\/)?' + // protocol
                  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
                  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
                  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
                  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
                  '(\\#[-a-z\\d_]*)?$',
                  'i'
                ); // fragment locator
                return !!pattern.test(value);
              } else {
                var pattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return pattern.test(value.toLowerCase());
              }
            } else {
              return false;
            }
          } else {
            return true;
          }
        },
      },
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          post_checkout_details: {
            thankyou_page: {
              validThankyouPage(value, details) {
                if (
                  details.dynamic_thank_you_page &&
                  details.dynamic_thank_you_page.enabled
                ) {
                  return true;
                }

                return required(value) && url(value);
              },
            },
          },
        },
      },
    },
  },
  computed: {
    contentSections() {
      return [
        {
          title: this.$t('frontend_product_step4_sales_page'),
          value: 'salespage',
          summary: this.localModel.sales_page || 'No sales page URL set',
        },
        {
          title: this.$t('frontend_product_step4_thankyou_page'),
          value: 'thankyoupage',
          summary: this.thankYouSummary,
        },
        {
          title: this.$t('frontend_product_step4_refund_period'),
          value: 'refund',
          summary: `${this.localModel.refund_period || 0} day refund period • ${this.localModel.waiting_period || 0} day waiting period`,
        },
        {
          title: this.$t('frontend_product_step4_support_info'),
          value: 'support_info',
          summary: this.supportInfoSummary,
        },
      ];
    },
    activeContentSection() {
      return this.contentSections.find((section) => section.value === this.contentsTab) || {
        title: this.$t('frontend_product_step4_contents'),
        summary: this.$t('frontend_product_step4_you_can_add'),
      };
    },
    thankYouSummary() {
      const dynamicCount = (this.localModel.pricing_points || []).filter((pricePoint) => {
        return pricePoint.post_checkout_details &&
          pricePoint.post_checkout_details.dynamic_thank_you_page &&
          pricePoint.post_checkout_details.dynamic_thank_you_page.enabled;
      }).length;

      if (dynamicCount) {
        return `${dynamicCount} dynamic thank-you ${dynamicCount === 1 ? 'page' : 'pages'} configured`;
      }

      return 'Custom thank-you URLs by price point';
    },
    supportInfoSummary() {
      const channelCount = this.supportChannelCount();

      if (channelCount) {
        return `${channelCount} support ${channelCount === 1 ? 'channel' : 'channels'} configured`;
      }

      return 'No support channels added';
    },
    contentsErrorTabs() {
      let contentErrorTabs = [];

      if (this.$v.localModel.sales_page.$error) {
        contentErrorTabs.push('salespage');
      }

      if (this.$v.localModel.pricing_points.$error) {
        contentErrorTabs.push('thankyoupage');
      }

      if (
        this.$v.localModel.refund_period.$error ||
        this.$v.localModel.waiting_period.$error
      ) {
        contentErrorTabs.push('refund');
      }

      if (this.$v.localModel.support_info_value.$error) {
        contentErrorTabs.push('support_info');
      }

      return contentErrorTabs;
    },
  },
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
  },
  created() { },
  methods: {
    handleUpdateModel(data) {
      const key = Object.keys(data)[0];
      if(Object.keys(this.localModel).includes(key)) {
        this.localModel[key] = data[key];
      }
    },
    validate() {
      this.$v.$touch();
      var isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    handleContentsTabChange(newTab) {
      this.contentsTab = newTab;
      this.showContentsEditorDrawer = true;
    },
    supportChannelCount() {
      if (!this.localModel.support_info_value) {
        return 0;
      }

      try {
        const parsed = JSON.parse(this.localModel.support_info_value);
        if (parsed && Array.isArray(parsed.channels)) {
          return parsed.channels.length;
        }
      } catch (e) {
        // Legacy single support values are counted below.
      }

      return this.localModel.is_support_enabled ? 1 : 0;
    },
    contentSectionError(section) {
      const messages = {
        salespage: 'Enter a complete sales-page URL, including https://.',
        thankyoupage: 'Every price point needs a valid thank-you URL or a dynamic thank-you page.',
        refund: 'Refund and waiting periods must be zero or a positive number.',
        support_info: 'Add a valid support channel or turn support information off.',
      };

      return messages[section] || 'Review this section before saving.';
    },
  },
};
</script>
<style scoped>

.gs-contents-step__head {
  margin-bottom: 18px;
}

.gs-contents-step__head h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-contents-step__head p {
  margin: 6px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-contents-step__sections {
  display: grid;
  gap: 10px;
  margin-bottom: 18px;
}

.gs-contents-step__section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  min-height: 72px;
  padding: 14px 16px;
  text-align: left;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  cursor: pointer;
  transition: background .18s ease, border-color .18s ease, box-shadow .18s ease;
}

.gs-contents-step__section:hover,
.gs-contents-step__section:focus-visible {
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-contents-step__section.is-active {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-contents-step__section.has-error:not(.is-active) {
  border-color: var(--gs-danger-border);
  background: #fffafb;
}

.gs-contents-step__section strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-contents-step__section span {
  display: block;
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.gs-contents-step__section .gs-contents-step__error-copy {
  margin-top: 7px;
  color: var(--gs-danger);
  font-size: 12px;
  font-weight: 600;
}

.gs-contents-step__section em {
  min-width: 76px;
  padding: 9px 14px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  text-align: center;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  opacity: 0;
  transition: opacity .16s ease, color .16s ease, border-color .16s ease, background .16s ease;
}

.gs-contents-step__section:hover em,
.gs-contents-step__section:focus-visible em,
.gs-contents-step__section:focus-within em {
  opacity: 1;
}

.gs-contents-step__section.is-active em {
  color: var(--gs-active);
  background: var(--gs-surface);
  border-color: var(--gs-active-border);
}

.gs-contents-step__drawer {
  min-height: 100%;
  background: var(--gs-surface);
}

.gs-contents-step__drawer-body {
  padding: 0;
}

</style>
